import { default as Alert } from 'antd/es/alert';
import { default as DatePicker } from 'antd/es/date-picker';
import { default as Modal } from 'antd/es/modal';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import { generateRangeFromType } from 'utils/dateRange';
import { API_DATE_FORMAT, DATE_FORMAT, formatDate } from 'utils/dayjs';

import {
  useGetCustomerCurrentUser,
  usePostDisbursementPayoutReports
} from 'api';

import { useOrganisationId } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';

import { BlindingDots } from '@atoms/BlindingDots/BlindingDots';
import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { CheckCircleSmallIcon } from '@atoms/Icon/CheckCircleSmallIcon';
import { DownloadThinIcon } from '@atoms/Icon/DownloadThinIcon';
import { RollingLoader } from '@atoms/RollingLoader/RollingLoader';
import { StatusTag } from '@atoms/StatusTag';

import { SelectVirtualAccount } from '@molecules/SelectVirtualAccount';

import { PAYOUT_STATUSES } from './constants';
import { useLocalState } from './localState';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const PayoutsExportDialog = ({ open, onClose }: Props) => {
  const { data } = useGetCustomerCurrentUser();
  const openNotification = useOpenNotification();

  const currentUserEmail = data ? data.user?.email : 'Loading...';

  const organisationId = useOrganisationId<number>();

  const today = dayjs().endOf('day');
  const { RangePicker } = DatePicker;

  const { filters: globalFilters } = useGlobalFilters();
  const { state: localState } = useLocalState();

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | undefined
  >(localState.filters.accountNumber);

  type DateRange = ReturnType<typeof generateRangeFromType>;
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(
    generateRangeFromType(globalFilters.dateRange)
  );

  const {
    mutateAsync: createPayoutsReport,
    isSuccess: creationSuccess,
    data: exportedPayouts,
    isPending: isExportedPayoutsLoading,
    reset: resetDialog
  } = usePostDisbursementPayoutReports();

  const onCreatePayoutReportRequest = async (event: {
    preventDefault: () => void;
  }) => {
    event.preventDefault();
    try {
      await createPayoutsReport({
        organisationId,
        data: {
          virtual_account_number:
            selectedAccountNumber === 'All' ? undefined : selectedAccountNumber,
          created_after: formatDate(selectedDateRange.from, 'utc', 'iso'),
          created_before: selectedDateRange.to
            ? formatDate(selectedDateRange.to, 'utc', 'iso')
            : dayjs().format(API_DATE_FORMAT),
          amount_to: localState.filters.amountTo,
          amount_from: localState.filters.amountFrom,
          ...(localState.filters.state &&
            localState.filters.state !== 'All' && {
              status_categories: [localState.filters.state]
            }),
          ...(localState.filters.currency !== 'All' && {
            currency_code: localState.filters.currency
          })
        }
      });
    } catch (error: any) {
      onClose();
      if (error?.error?.code === 1001) {
        openNotification({
          kind: 'error',
          message:
            'No requests found for the selected filters. Please adjust your filters and try again.'
        });
      } else {
        openNotification({
          kind: 'error',
          message: 'Something went wrong'
        });
      }
    }
  };

  const onDialogClose = () => {
    resetDialog();
    onClose();
  };

  // Validations
  const showCurrency = localState.filters.currency !== 'All';
  const showAmountRange =
    localState.filters.amountTo || localState.filters.amountFrom;

  const invalidDateRange = (startDate: string, endDate: string | undefined) => {
    // date range can't exceed 90 days
    const start = dayjs(startDate, DATE_FORMAT);
    const end = dayjs(endDate, DATE_FORMAT);
    const diff = end.diff(start, 'day') + 1;

    return diff > 90;
  };

  const disableExports =
    // account number and date range are required
    invalidDateRange(selectedDateRange.from, selectedDateRange.to);

  const showDialog = !isExportedPayoutsLoading && !creationSuccess;
  const showLoading = isExportedPayoutsLoading && !creationSuccess;
  const showSuccess =
    creationSuccess && exportedPayouts && !isExportedPayoutsLoading;

  // Effects
  useEffect(() => {
    setSelectedAccountNumber(localState.filters.accountNumber);
  }, [localState.filters.accountNumber]);

  useEffect(() => {
    setSelectedDateRange(generateRangeFromType(globalFilters.dateRange));
  }, [globalFilters.dateRange]);

  return (
    <Modal
      title={
        <div className="mb-6 flex flex-row">
          <DownloadThinIcon />
          <div className="pl-4">Export payout requests</div>
        </div>
      }
      open={open}
      styles={{
        mask: {
          backgroundColor: 'var(--tenant-surface-overlay)'
        },
        content: {
          paddingTop: 12,
          backgroundColor: 'rgb(255, 255, 255)'
        }
      }}
      onOk={onDialogClose}
      width={336}
      onCancel={onDialogClose}
      footer={[
        showSuccess ? (
          <PrimaryButton
            onClick={onDialogClose}
            className="w-full"
            size="large"
          >
            Done
          </PrimaryButton>
        ) : (
          <PrimaryButton
            onClick={onCreatePayoutReportRequest}
            disabled={disableExports}
            className="w-full"
            size="large"
          >
            Export CSV file
          </PrimaryButton>
        )
      ]}
    >
      {showDialog && (
        <div className="my-8 flex flex-col gap-4 rounded-2xl p-0 text-sm">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-content-secondary">
              Date range
            </label>
            {invalidDateRange(selectedDateRange.from, selectedDateRange.to) && (
              <Alert
                className="h-[30px]"
                message="Please choose less than 90 days"
                type="warning"
                showIcon
              />
            )}
            <RangePicker
              className="h-10 rounded-[20px]"
              format={DATE_FORMAT}
              allowClear={false}
              maxDate={today}
              value={[
                dayjs(selectedDateRange.from, DATE_FORMAT),
                dayjs(selectedDateRange.to, DATE_FORMAT)
              ]}
              onChange={(_, dates) => {
                setSelectedDateRange({
                  type: 'custom',
                  from: dates[0],
                  to: dates[1]
                });
              }}
              placeholder={['Borderless', 'Borderless']}
              variant="outlined"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-content-secondary">
              Account
            </label>
            <SelectVirtualAccount
              className="w-full"
              onChangeCallback={(value) => setSelectedAccountNumber(value)}
              currentAccountNumber={selectedAccountNumber}
            />
          </div>

          {showCurrency && (
            <div>
              <div className="text-xs font-semibold text-content-secondary">
                Currency
              </div>
              <span className="font-bold">{localState.filters.currency}</span>
            </div>
          )}

          {showAmountRange && (
            <div>
              <div className="pb-0.5 text-xs font-semibold text-content-secondary">
                Amount range
              </div>
              <span className="font-bold">
                {`${localState.filters.amountFrom ? localState.filters.amountFrom : '0'} - ${localState.filters.amountTo ? localState.filters.amountTo : '∞'}`}
              </span>
            </div>
          )}
          {localState.filters.state !== 'All' &&
            localState.filters.state !== undefined && (
              <div>
                <div className="pb-1 text-xs font-semibold text-content-secondary">
                  Status
                </div>
                <div className="flex justify-start font-bold">
                  <StatusTag
                    status={localState.filters.state}
                    statuses={PAYOUT_STATUSES}
                  />
                </div>
              </div>
            )}
        </div>
      )}

      {showLoading && (
        <div className="size-78 flex justify-center gap-4 p-4">
          <RollingLoader ariaLabel="Creating payout export" />
          <div className="text-sm">
            <span className="invisible">...</span>
            <span>Processing</span>
            <BlindingDots />
          </div>
        </div>
      )}
      {showSuccess && (
        <div className="flex flex-col items-center bg-white">
          <div className="mx-auto mt-0 flex size-12 items-center justify-center rounded-full">
            <CheckCircleSmallIcon />
          </div>
          <p className="text-center text-lg font-medium">
            Your report is being generated
          </p>
          <div className="px-2 py-4 text-center text-sm">
            When the report is ready you will receive an email
            <br />
            <span className="font-bold text-content-brand">
              {currentUserEmail}
            </span>
            <br />
            with a link where you can download your report.
          </div>
        </div>
      )}
    </Modal>
  );
};
